import React, { FC, useContext, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useFormContext } from 'react-hook-form';
import { Button } from '../Button';
import { Section } from '../Section';
import { Container } from '../Container';
import { SelectButton } from '../forms/SelectButton';
import { SEARCH_ORDER, SEARCH_SOLD_ORDER, SEARCH_LET_ORDER } from '../../const';
import { Sort } from '../icon/icons/Sort';
import { MapListingContext } from '../../context/MapListingContext';
import { UiStateContext } from '../../context/UiStateContext';
import { UserContext } from '../../context/UserContext';
import { MyKfhCreateSavedSearchInput } from '../../types/graphqlTypes';
import { useCurrencies } from '../../hooks/useCurrencies';
import { CurrencyCodeType, getCurrencyName } from '../../utils/getCurrencyName';
import { Dollar, Euro, Pound, MapView, ListView, Alert } from '../icon';
import { getResponsiveType } from '../../utils';

interface SearchActionsProps {
  disable?: boolean;
  disableSort?: boolean;
  disableAlerts?: boolean;
  savedSearch: MyKfhCreateSavedSearchInput;
  searchOrderType: 'DEFAULT' | 'SOLD' | 'LET';
}

const StyledActionLinks = styled.nav`
  ${({ theme: { space, media } }) => css`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: ${space.sm};

    @media (min-width: ${media.md}) {
      justify-content: flex-end;
    }

    .list-view-toggle-lg {
      margin-right: auto;
      display: none;

      @media (min-width: ${media.md}) {
        display: flex;
      }
    }
  `};
`;

const StyledSortActions = styled.div`
  ${({ theme: { space, media } }) => css`
    display: flex;
    gap: ${space.sm};
  `};
`;

const StyledAlertText = styled.span`
  ${({ theme: { maxMedia } }) => css`
    @media (max-width: ${maxMedia.sm}) {
      display: none;
    }
  `};
`;

// Very specific component overides for small mobiles -
const StyledSort = styled(SelectButton)`
  ${({ theme: { maxMedia } }) => css`
    @media (max-width: 450px) {
      &:after {
        content: 'Sort';
        display: block;
        ${getResponsiveType('bodySmall')}
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        left: 40px;
        font-weight: 700;
        pointer-events: none;
      }

      // Visually hide the select text
      select {
        color: #fff;
        width: 86px;

        &:hover,
        &:disabled {
          color: #fff;
        }
      }
    }
  `};
`;

export const SearchActions: FC<SearchActionsProps> = ({
  disable,
  disableSort,
  savedSearch,
  disableAlerts,
  searchOrderType,
}) => {
  const {
    register,
    watch,
    formState: { isSubmitting },
  } = useFormContext();
  const { activeView, setActiveView } = useContext(MapListingContext);
  const { setActiveBlade } = useContext(UiStateContext);
  const { user, setRegisterData } = useContext(UserContext);
  const { currencies, handleSetCurrency, current } = useCurrencies();

  const toggleActiveView = () => {
    setActiveView(activeView === 'map' ? 'list' : 'map');
  };

  const handleCreateAlert = () => {
    // Check if user is logged in

    // if yes then open create alert blade

    if (user) {
      setActiveBlade('createPropertyAlert');
    } else {
      setRegisterData({
        action: 'saveSearch',
        data: savedSearch,
      });
      setActiveBlade('createPropertyAlertLoginOrRegister');
    }
  };

  // Handle currency

  const currencyid = watch('currencyid');
  // If the currency changes we need to sync the context with the new currency
  useEffect(() => {
    handleSetCurrency(currencyid as number);
  }, [currencyid, handleSetCurrency]);

  const currencyName = getCurrencyName(current?.code as CurrencyCodeType);

  const getCurrencyIcon = (currencyId) => {
    switch (currencyId) {
      case 'Dollar':
        return <Dollar />;
      case 'Euro':
        return <Euro />;
      default:
        return <Pound />;
    }
  };

  const getSearchOrder = () => {
    switch (searchOrderType) {
      case 'LET':
        return SEARCH_LET_ORDER;
      case 'SOLD':
        return SEARCH_SOLD_ORDER;
      default:
        return SEARCH_ORDER;
    }
  };

  return (
    <>
      <Section tight>
        <Container>
          <StyledActionLinks>
            <Button
              variant="pill"
              onClick={toggleActiveView}
              type="button"
              iconLeft={activeView === 'list' ? <MapView /> : <ListView />}
              disabled={disable}
              className="list-view-toggle-lg"
            >
              {activeView === 'list' ? `Map View` : `List View`}
            </Button>

            <StyledSortActions>
              {activeView === 'list' && !disableSort && (
                <>
                  <StyledSort
                    disabled={disable}
                    id="property-order"
                    options={getSearchOrder()}
                    label="Sort Results"
                    iconLeft={<Sort />}
                    {...register('sort')}
                  />
                </>
              )}

              <SelectButton
                options={currencies.map(({ node }) => ({ name: node.code, value: node.remoteId }))}
                label="Currency"
                id="currencyid"
                disabled={disable}
                iconLeft={getCurrencyIcon(currencyName)}
                {...register('currencyid', {
                  valueAsNumber: true,
                })}
              />
            </StyledSortActions>

            {!disableAlerts && (
              <Button
                variant="pill"
                onClick={() => handleCreateAlert()}
                type="button"
                iconLeft={<Alert />}
                disabled={disable}
                disableClickTracking
              >
                Create <StyledAlertText>an email </StyledAlertText>alert
              </Button>
            )}
          </StyledActionLinks>
        </Container>
      </Section>
    </>
  );
};
